/**
 * @typedef {"inline" | "modal" | "modal_and_inline"} CourtNoticeMode
 * @typedef {"every_page_view" | "session" | "days"} CourtNoticeRepeat
 * @typedef {"inline" | "modal"} CourtNoticeSurface
 * @typedef {"not_applicable" | "pending" | "included" | "excluded" | "unknown"} CourtNoticeGeoState
 *
 * @typedef {{
 *   sectionId: string;
 *   mode: CourtNoticeMode;
 *   previewTools: boolean;
 *   designMode: boolean;
 *   repeat: CourtNoticeRepeat;
 *   repeatDays: number;
 *   inlineDismissible: boolean;
 *   closeOnBackdrop: boolean;
 *   storageKey: string;
 *   geo: {
 *     scope: "global" | "allowlist";
 *     region: "global" | "europe" | "countries";
 *     source: "visitor_ip" | "storefront_country";
 *     countries: string[];
 *     unknown: "show" | "hide";
 *     server: string;
 *   };
 * }} CourtNoticeConfig
 */

const COURT_NOTICE_CONFIG_ID = "court-notice-config";
const COURT_NOTICE_DAY_MS = 86400000;
const COURT_NOTICE_COUNTRY_CACHE_KEY = "country_code_cache_v1";
const COURT_NOTICE_COUNTRY_CACHE_TTL_MS = COURT_NOTICE_DAY_MS;
const COURT_NOTICE_COUNTRY_TIMEOUT_MS = 2500;
const COURT_NOTICE_COUNTRY_DEADLINE_MS = 4000;
const COURT_NOTICE_PREVIEW_PARAM = "court_notice_preview";
const COURT_NOTICE_STATUS_PARAM = "court_notice_status";
const COURT_NOTICE_MODES = ["inline", "modal", "modal_and_inline"];

/** @type {{ lastFocused: Element | null; geoLookupStarted: boolean }} */
const courtNoticeRuntime = { lastFocused: null, geoLookupStarted: false };

const courtNoticeRoot = document.documentElement;

/** @type {() => CourtNoticeConfig | null} */
const readCourtNoticeConfig = () => {
  const element = document.getElementById(COURT_NOTICE_CONFIG_ID);
  if (element === null) {
    return null;
  }
  try {
    return /** @type {CourtNoticeConfig} */ (JSON.parse(element.textContent ?? ""));
  } catch {
    return null;
  }
};

/** @type {(storage: "local" | "session", key: string) => string | null} */
const readCourtNoticeStorage = (storage, key) => {
  try {
    return (storage === "local" ? globalThis.localStorage : globalThis.sessionStorage).getItem(key);
  } catch {
    return null;
  }
};

/** @type {(storage: "local" | "session", key: string, value: string | null) => void} */
const writeCourtNoticeStorage = (storage, key, value) => {
  try {
    const target = storage === "local" ? globalThis.localStorage : globalThis.sessionStorage;
    if (value === null) {
      target.removeItem(key);
    } else {
      target.setItem(key, value);
    }
  } catch {
    return;
  }
};

/** @type {(config: CourtNoticeConfig) => CourtNoticeMode} */
const getCourtNoticeMode = (config) => {
  const requested = new URL(globalThis.location.href).searchParams.get(COURT_NOTICE_PREVIEW_PARAM);
  if (config.previewTools && requested !== null && COURT_NOTICE_MODES.includes(requested)) {
    return /** @type {CourtNoticeMode} */ (requested);
  }
  return config.mode;
};

/** @type {(mode: CourtNoticeMode) => boolean} */
const modeHasInline = (mode) => mode === "inline" || mode === "modal_and_inline";

/** @type {(mode: CourtNoticeMode) => boolean} */
const modeHasModal = (mode) => mode === "modal" || mode === "modal_and_inline";

/** @type {(config: CourtNoticeConfig, surface: CourtNoticeSurface) => string} */
const courtNoticeMemoryKey = (config, surface) => `${config.storageKey}:${surface}`;

/** @type {(config: CourtNoticeConfig, surface: CourtNoticeSurface, nowMs: number) => boolean} */
const isCourtNoticeSuppressed = (config, surface, nowMs) => {
  const key = courtNoticeMemoryKey(config, surface);
  if (config.repeat === "session") {
    return readCourtNoticeStorage("session", key) === "1";
  }
  if (config.repeat === "days") {
    const expiresAt = Number(readCourtNoticeStorage("local", key));
    return Number.isFinite(expiresAt) && expiresAt > nowMs;
  }
  return false;
};

/** @type {(config: CourtNoticeConfig, surface: CourtNoticeSurface, nowMs: number) => void} */
const rememberCourtNoticeClosed = (config, surface, nowMs) => {
  const key = courtNoticeMemoryKey(config, surface);
  if (config.repeat === "session") {
    writeCourtNoticeStorage("session", key, "1");
  } else if (config.repeat === "days") {
    writeCourtNoticeStorage("local", key, String(nowMs + Math.max(1, config.repeatDays) * COURT_NOTICE_DAY_MS));
  }
};

/** @type {(config: CourtNoticeConfig) => void} */
const forgetCourtNoticeClosed = (config) => {
  for (const surface of /** @type {CourtNoticeSurface[]} */ (["inline", "modal"])) {
    writeCourtNoticeStorage("session", courtNoticeMemoryKey(config, surface), null);
    writeCourtNoticeStorage("local", courtNoticeMemoryKey(config, surface), null);
  }
  delete courtNoticeRoot.dataset.courtNoticeInlineClosed;
};

/** @type {(config: CourtNoticeConfig) => boolean} */
const usesVisitorGeo = (config) => config.geo.scope === "allowlist" && config.geo.source === "visitor_ip";

/** @type {(value: unknown) => string | null} */
const normalizeCountryCode = (value) => {
  const code = typeof value === "string" ? value.trim().toUpperCase() : "";
  return /^[A-Z]{2}$/.test(code) ? code : null;
};

/** @type {() => string | null} */
const readCachedCountry = () => {
  const raw = readCourtNoticeStorage("local", COURT_NOTICE_COUNTRY_CACHE_KEY);
  if (raw === null) {
    return null;
  }
  try {
    /** @type {{ code?: unknown; ts?: unknown }} */
    const parsed = JSON.parse(raw);
    const isFresh = typeof parsed.ts === "number" && Date.now() - parsed.ts < COURT_NOTICE_COUNTRY_CACHE_TTL_MS;
    return isFresh ? normalizeCountryCode(parsed.code) : null;
  } catch {
    return null;
  }
};

/** @type {(config: CourtNoticeConfig, country: string | null) => CourtNoticeGeoState} */
const geoStateForCountry = (config, country) => {
  if (country === null) {
    return "unknown";
  }
  return config.geo.countries.includes(country) ? "included" : "excluded";
};

/** @type {(config: CourtNoticeConfig) => CourtNoticeGeoState} */
const getCourtNoticeGeoState = (config) => {
  if (!usesVisitorGeo(config)) {
    return "not_applicable";
  }
  const current = courtNoticeRoot.dataset.courtNoticeGeo;
  if (current === "included" || current === "excluded" || current === "unknown") {
    return current;
  }
  const cached = readCachedCountry();
  return cached === null ? "pending" : geoStateForCountry(config, cached);
};

/** @type {(config: CourtNoticeConfig, geo: CourtNoticeGeoState, surface: CourtNoticeSurface) => boolean} */
const geoAllows = (config, geo, surface) => {
  if (geo === "excluded") {
    return false;
  }
  if (geo === "unknown") {
    return config.geo.unknown === "show";
  }
  if (geo === "pending") {
    return surface === "inline";
  }
  return true;
};

/** @type {() => HTMLDialogElement | null} */
const getCourtNoticeDialog = () => document.querySelector("dialog[data-court-notice-dialog]");

/** @type {() => boolean} */
const supportsCourtNoticeDialog = () =>
  typeof HTMLDialogElement === "function" && "showModal" in HTMLDialogElement.prototype;

/** @type {(config: CourtNoticeConfig, dialog: HTMLDialogElement, byUser: boolean) => void} */
const closeCourtNoticeDialog = (config, dialog, byUser) => {
  if (byUser) {
    rememberCourtNoticeClosed(config, "modal", Date.now());
  }
  if (dialog.open) {
    dialog.close();
  }
};

/** @type {(config: CourtNoticeConfig, nowMs: number) => boolean} */
const shouldShowInline = (config, nowMs) => {
  const mode = getCourtNoticeMode(config);
  const usesFallback = mode === "modal" && !supportsCourtNoticeDialog();
  if (!modeHasInline(mode) && !usesFallback) {
    return false;
  }
  if (!geoAllows(config, getCourtNoticeGeoState(config), "inline")) {
    return false;
  }
  if (!config.inlineDismissible) {
    return true;
  }
  return (
    courtNoticeRoot.dataset.courtNoticeInlineClosed !== "true" && !isCourtNoticeSuppressed(config, "inline", nowMs)
  );
};

/** @type {(config: CourtNoticeConfig) => boolean} */
const canShowModal = (config) =>
  modeHasModal(getCourtNoticeMode(config)) &&
  supportsCourtNoticeDialog() &&
  geoAllows(config, getCourtNoticeGeoState(config), "modal");

/** @type {(config: CourtNoticeConfig, force: boolean) => void} */
const openCourtNoticeModal = (config, force) => {
  const dialog = getCourtNoticeDialog();
  const nowMs = Date.now();
  if (dialog === null || dialog.open || !canShowModal(config)) {
    return;
  }
  if (!force && (dialog.dataset.courtNoticeShown === "true" || isCourtNoticeSuppressed(config, "modal", nowMs))) {
    return;
  }
  dialog.dataset.courtNoticeShown = "true";
  courtNoticeRuntime.lastFocused = document.activeElement;
  dialog.showModal();
  courtNoticeRoot.classList.add("court-notice-modal-open");
};

/** @type {(config: CourtNoticeConfig) => void} */
const renderCourtNoticeClientStatus = (config) => {
  const target = document.querySelector("[data-court-notice-client-status]");
  if (!(target instanceof HTMLElement)) {
    return;
  }
  const geo = getCourtNoticeGeoState(config);
  const country = courtNoticeRoot.dataset.courtNoticeCountry ?? readCachedCountry() ?? "none";
  target.textContent = [
    `display mode ${getCourtNoticeMode(config)}`,
    `geography ${geo}${usesVisitorGeo(config) ? ` (detected ${country})` : ""}`,
    `inline banner ${courtNoticeRoot.dataset.courtNoticeInline ?? "unknown"}`,
    `popup closed earlier ${isCourtNoticeSuppressed(config, "modal", Date.now()) ? "yes" : "no"}`,
  ].join("; ");
};

/** @type {(config: CourtNoticeConfig) => void} */
const applyCourtNoticeState = (config) => {
  courtNoticeRoot.dataset.courtNoticeMode = getCourtNoticeMode(config);
  courtNoticeRoot.dataset.courtNoticeInline = shouldShowInline(config, Date.now()) ? "shown" : "hidden";
  courtNoticeRoot.dataset.courtNoticeReady = "true";
  const dialog = getCourtNoticeDialog();
  if (dialog !== null && dialog.open && !canShowModal(config)) {
    closeCourtNoticeDialog(config, dialog, false);
  }
  renderCourtNoticeClientStatus(config);
};

/** @type {(url: string, signal: AbortSignal) => Promise<Response | null>} */
const fetchCountryProvider = async (url, signal) => {
  try {
    return await fetch(url, { signal, credentials: "omit" });
  } catch {
    return null;
  }
};

/** @type {(response: Response | null) => Promise<Record<string, unknown> | null>} */
const readProviderJson = async (response) => {
  if (response === null || !response.ok) {
    return null;
  }
  try {
    const parsed = await response.json();
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
};

/** @type {(response: Response | null) => Promise<string | null>} */
const readProviderText = async (response) => {
  if (response === null || !response.ok) {
    return null;
  }
  try {
    return await response.text();
  } catch {
    return null;
  }
};

/** @type {Array<(signal: AbortSignal) => Promise<string | null>>} */
const courtNoticeCountryProviders = [
  async (signal) => {
    const json = await readProviderJson(await fetchCountryProvider("https://ipwho.is/", signal));
    return json !== null && json.success !== false ? normalizeCountryCode(json.country_code) : null;
  },
  async (signal) => {
    const json = await readProviderJson(await fetchCountryProvider("https://get.geojs.io/v1/ip/country.json", signal));
    return json === null ? null : normalizeCountryCode(json.country);
  },
  async (signal) => {
    const json = await readProviderJson(await fetchCountryProvider("https://api.country.is/", signal));
    return json === null ? null : normalizeCountryCode(json.country);
  },
  async (signal) => {
    const text = await readProviderText(await fetchCountryProvider("https://www.cloudflare.com/cdn-cgi/trace", signal));
    const match = text === null ? null : /\bloc=([A-Z]{2})\b/.exec(text);
    return match === null ? null : normalizeCountryCode(match[1]);
  },
];

/** @type {() => Promise<string | null>} */
const lookupVisitorCountry = async () => {
  const deadline = Date.now() + COURT_NOTICE_COUNTRY_DEADLINE_MS;
  for (const provider of courtNoticeCountryProviders) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      return null;
    }
    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(
      () => controller.abort(),
      Math.min(COURT_NOTICE_COUNTRY_TIMEOUT_MS, remaining),
    );
    const code = await provider(controller.signal);
    globalThis.clearTimeout(timeoutId);
    if (code !== null) {
      return code;
    }
  }
  return null;
};

/** @type {(config: CourtNoticeConfig) => Promise<void>} */
const resolveVisitorGeo = async (config) => {
  if (!usesVisitorGeo(config) || courtNoticeRuntime.geoLookupStarted) {
    return;
  }
  courtNoticeRuntime.geoLookupStarted = true;
  const cached = readCachedCountry();
  const country = cached ?? (await lookupVisitorCountry());
  if (country !== null && cached === null) {
    writeCourtNoticeStorage("local", COURT_NOTICE_COUNTRY_CACHE_KEY, JSON.stringify({ code: country, ts: Date.now() }));
  }
  courtNoticeRoot.dataset.courtNoticeCountry = country ?? "unknown";
  courtNoticeRoot.dataset.courtNoticeGeo = geoStateForCountry(config, country);
  applyCourtNoticeState(config);
  if (!config.designMode) {
    openCourtNoticeModal(config, false);
  }
};

/** @type {() => void} */
const updateCourtNoticePreviewLinks = () => {
  for (const link of document.querySelectorAll("a[data-court-notice-preview-link]")) {
    if (link instanceof HTMLAnchorElement) {
      const url = new URL(globalThis.location.href);
      const mode = link.dataset.courtNoticePreviewLink ?? "";
      if (mode === "") {
        url.searchParams.delete(COURT_NOTICE_PREVIEW_PARAM);
      } else {
        url.searchParams.set(COURT_NOTICE_PREVIEW_PARAM, mode);
      }
      url.searchParams.set(COURT_NOTICE_STATUS_PARAM, "1");
      link.href = url.toString();
    }
  }
  const status = document.querySelector("[data-court-notice-status]");
  if (
    status instanceof HTMLElement &&
    new URL(globalThis.location.href).searchParams.get(COURT_NOTICE_STATUS_PARAM) === "1"
  ) {
    status.hidden = false;
  }
};

/** @type {(config: CourtNoticeConfig, button: HTMLElement) => void} */
const handleCourtNoticeDismiss = (config, button) => {
  const nowMs = Date.now();
  if (button.dataset.courtNoticeDismiss === "modal") {
    const dialog = getCourtNoticeDialog();
    if (dialog !== null) {
      closeCourtNoticeDialog(config, dialog, true);
    }
    return;
  }
  courtNoticeRoot.dataset.courtNoticeInlineClosed = "true";
  rememberCourtNoticeClosed(config, "inline", nowMs);
  applyCourtNoticeState(config);
  const main = document.getElementById("main-content");
  if (main !== null) {
    main.focus();
  }
};

/** @type {(event: Event) => void} */
const handleCourtNoticeClick = (event) => {
  const config = readCourtNoticeConfig();
  const target = event.target;
  if (config === null || !(target instanceof Element)) {
    return;
  }
  const dismiss = target.closest("[data-court-notice-dismiss]");
  if (dismiss instanceof HTMLElement) {
    handleCourtNoticeDismiss(config, dismiss);
    return;
  }
  if (target.closest("[data-court-notice-reset]") !== null) {
    forgetCourtNoticeClosed(config);
    applyCourtNoticeState(config);
    openCourtNoticeModal(config, true);
    return;
  }
  if (config.closeOnBackdrop && target instanceof HTMLDialogElement && target.matches("[data-court-notice-dialog]")) {
    closeCourtNoticeDialog(config, target, true);
  }
};

/** @type {(event: Event) => void} */
const handleCourtNoticeDialogClose = (event) => {
  const dialog = event.target;
  const config = readCourtNoticeConfig();
  if (!(dialog instanceof HTMLDialogElement) || !dialog.matches("[data-court-notice-dialog]") || config === null) {
    return;
  }
  courtNoticeRoot.classList.remove("court-notice-modal-open");
  const previous = courtNoticeRuntime.lastFocused;
  courtNoticeRuntime.lastFocused = null;
  if (previous instanceof HTMLElement && previous.isConnected && previous !== document.body) {
    previous.focus();
  }
  applyCourtNoticeState(config);
};

/** @type {(event: Event) => void} */
const handleCourtNoticeDialogCancel = (event) => {
  const config = readCourtNoticeConfig();
  if (
    config !== null &&
    event.target instanceof HTMLDialogElement &&
    event.target.matches("[data-court-notice-dialog]")
  ) {
    rememberCourtNoticeClosed(config, "modal", Date.now());
  }
};

/** @type {(event: KeyboardEvent) => void} */
const handleCourtNoticeKeydown = (event) => {
  const dialog = getCourtNoticeDialog();
  if (event.key !== "Tab" || dialog === null || !dialog.open) {
    return;
  }
  /** @type {HTMLElement[]} */
  const focusable = Array.from(dialog.querySelectorAll("a[href], button:not([disabled])"));
  if (focusable.length === 0) {
    return;
  }
  event.preventDefault();
  const index = focusable.findIndex((element) => element === document.activeElement);
  const step = event.shiftKey ? -1 : 1;
  const next = index === -1 ? (event.shiftKey ? focusable.length - 1 : 0) : index + step;
  focusable[(next + focusable.length) % focusable.length]?.focus();
};

/** @type {() => void} */
const recheckCourtNotice = () => {
  const config = readCourtNoticeConfig();
  if (config !== null) {
    applyCourtNoticeState(config);
  }
};

/** @type {(event: Event) => void} */
const handleCourtNoticeSectionEvent = (event) => {
  const detail = /** @type {CustomEvent<{ sectionId?: string }>} */ (event).detail;
  const config = readCourtNoticeConfig();
  if (config === null || detail?.sectionId !== config.sectionId) {
    return;
  }
  if (event.type === "shopify:section:unload") {
    courtNoticeRoot.classList.remove("court-notice-modal-open");
    delete courtNoticeRoot.dataset.courtNoticeInline;
    return;
  }
  updateCourtNoticePreviewLinks();
  applyCourtNoticeState(config);
  if (event.type === "shopify:section:select") {
    openCourtNoticeModal(config, true);
  }
};

/** @type {() => void} */
const startCourtNotice = () => {
  const config = readCourtNoticeConfig();
  if (config === null) {
    return;
  }
  updateCourtNoticePreviewLinks();
  applyCourtNoticeState(config);
  if (!config.designMode) {
    openCourtNoticeModal(config, false);
  }
  void resolveVisitorGeo(config);
};

/** @type {() => void} */
const bindCourtNotice = () => {
  if (courtNoticeRoot.dataset.courtNoticeBound === "true") {
    return;
  }
  courtNoticeRoot.dataset.courtNoticeBound = "true";
  document.addEventListener("click", handleCourtNoticeClick);
  document.addEventListener("close", handleCourtNoticeDialogClose, true);
  document.addEventListener("cancel", handleCourtNoticeDialogCancel, true);
  document.addEventListener("keydown", handleCourtNoticeKeydown);
  document.addEventListener("shopify:section:load", handleCourtNoticeSectionEvent);
  document.addEventListener("shopify:section:unload", handleCourtNoticeSectionEvent);
  document.addEventListener("shopify:section:select", handleCourtNoticeSectionEvent);
  globalThis.addEventListener("pageshow", recheckCourtNotice);
  const initialConfig = readCourtNoticeConfig();
  if (initialConfig !== null) {
    applyCourtNoticeState(initialConfig);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startCourtNotice, { once: true });
  } else {
    startCourtNotice();
  }
};

bindCourtNotice();
